import useGenerateScriptCompliance from "@entities/brief/lib/hooks/scripting/useGenerateScriptCompliance";
import useGetScriptingCompliance from "@entities/brief/lib/hooks/scripting/useGetScriptingCompliance";
// import { BriefAiDrawerButton } from "@features/BriefAIDrawerButton";
import { IGetScriptingResponse } from "@shared/lib/types/briefCampains";
import { ComplianceCheckStatusReportEnum } from "@shared/lib/types/drafting";
import { IScriptCompliance } from "@shared/lib/types/scripting";
import { Flex, Space } from "antd";
import classNames from "classnames";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Loader2,
  RotateCw,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./ScriptPageHeader.module.scss";

interface ScriptPageHeaderProps {
  serverScript?: IGetScriptingResponse;
  refetch: () => void;
  onClose?: () => void;
}

export function ScriptPageHeader({
  serverScript,
  refetch,
  onClose,
}: ScriptPageHeaderProps) {
  const scriptId = serverScript?.script?._id || "";

  const complianceParams: IScriptCompliance = useMemo(
    () => ({
      scriptId: scriptId,
    }),
    [scriptId]
  );

  const hasComplianceParams = !!complianceParams.scriptId;

  const {
    scriptingComplianceInfo,
    isScriptingComplianceFetching,
    scriptingComplianceRefetch,
  } = useGetScriptingCompliance(complianceParams);

  const {
    generateScriptCompliance,
    isGeneratingScriptCompliance,
    isGeneratingScriptComplianceSuccess,
  } = useGenerateScriptCompliance();

  const complianceReport = scriptingComplianceInfo?.data?.complianceCheckReport;
  const complianceCheckStatus =
    scriptingComplianceInfo?.data?.complianceCheckStatus;

  const complianceScore = complianceReport?.score ?? 0;

  const isAiPerformanceChecks = complianceReport?.isAiPerformanceChecks;
  const isAiBrandGuardRailsChecks = complianceReport?.isAiBrandGuardRailsChecks;
  const isAiAdvancedPromptingChecks =
    complianceReport?.isAiAdvancedPromptingChecks;
  const isAiConsistencyCheck = complianceReport?.isAiConsistencyCheck;
  const isAiSafetyChecks = complianceReport?.isAiSafetyChecks;
  const isAiScriptVsDraftingConsistencyCheck =
    complianceReport?.isAiScriptVsDraftingConsistencyCheck;

  const aiPerformanceChecks =
    complianceReport?.performanceGuidelinesChecks || [];
  const aiBrandGuardRailsChecks = complianceReport?.brandGuardRailsChecks || [];
  const aiAdvancedPromptingChecks =
    complianceReport?.advancedPromptingChecks || [];
  const aiConsistencyChecks = complianceReport?.consistencyChecks || [];
  const aiSafetyChecks = complianceReport?.safetyChecks || [];
  const aiScriptVsDraftingConsistencyChecks =
    complianceReport?.scriptVsDraftingConsistencyChecks || [];

  const allChecks = [
    ...(isAiPerformanceChecks ? aiPerformanceChecks : []),
    ...(isAiBrandGuardRailsChecks ? aiBrandGuardRailsChecks : []),
    ...(isAiAdvancedPromptingChecks ? aiAdvancedPromptingChecks : []),
    ...(isAiConsistencyCheck ? aiConsistencyChecks : []),
    ...(isAiSafetyChecks ? aiSafetyChecks : []),
    ...(isAiScriptVsDraftingConsistencyCheck
      ? aiScriptVsDraftingConsistencyChecks
      : []),
  ];

  const failCount = allChecks.filter(
    (check) => check.status === ComplianceCheckStatusReportEnum.NonCompliant
  ).length;
  const warningCount = allChecks.filter(
    (check) =>
      check.status === ComplianceCheckStatusReportEnum.PartiallyCompliant
  ).length;

  useEffect(() => {
    if (isGeneratingScriptComplianceSuccess && scriptingComplianceRefetch) {
      scriptingComplianceRefetch();
    }
  }, [isGeneratingScriptComplianceSuccess, scriptingComplianceRefetch]);

  const isRunningAnalysis =
    complianceCheckStatus === "Pending" ||
    isGeneratingScriptCompliance ||
    isScriptingComplianceFetching;

  const complianceStatusConfig = useMemo(() => {
    if (isRunningAnalysis) {
      return {
        label: "Analyzing",
        tone: "pending" as const,
      };
    }

    switch (complianceReport?.status) {
      case ComplianceCheckStatusReportEnum.Compliant:
        return { label: "Compliant", tone: "success" as const };
      case ComplianceCheckStatusReportEnum.PartiallyCompliant:
        return { label: "Needs Attention", tone: "warning" as const };
      case ComplianceCheckStatusReportEnum.NonCompliant:
        return { label: "Non-Compliant", tone: "error" as const };
      default:
        return { label: "Compliance", tone: "neutral" as const };
    }
  }, [complianceCheckStatus, complianceReport?.status, isRunningAnalysis]);

  const complianceMessages = useMemo(
    () => [
      "AI signal scan in progress...",
      "Policy alignment underway...",
      "Guardrails recalibration...",
      "Checking compliance...",
      "Validating Accuracy...",
      "Analyzing Performance...",
      "Evaluating Engagement...",
      "Checking Brand Alignment...",
      "Analyzing Compliance...",
      "Validating Accuracy...",
      "Verifying Consistency...",
    ],
    []
  );
  const [complianceMessageIndex, setComplianceMessageIndex] = useState(0);

  useEffect(() => {
    if (!isRunningAnalysis) {
      setComplianceMessageIndex(0);
      return;
    }
    const id = setInterval(() => {
      setComplianceMessageIndex(
        (prev) => (prev + 1) % complianceMessages.length
      );
    }, 10000);
    return () => clearInterval(id);
  }, [isRunningAnalysis, complianceMessages.length]);

  // Extract creator info
  const creatorName =
    serverScript?.campaignInfluencer?.influencer?.firstName +
    " " +
    serverScript?.campaignInfluencer?.influencer?.lastName;
  const creatorHandle = serverScript?.campaignInfluencer?.socialMedia?.username;
  const profilePicUrl =
    serverScript?.campaignInfluencer?.socialMedia?.profilePicUrl || "";

  // Compliance dropdown state
  const [isComplianceDropdownOpen, setIsComplianceDropdownOpen] =
    useState(false);
  const complianceDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        complianceDropdownRef.current &&
        !complianceDropdownRef.current.contains(event.target as Node)
      ) {
        setIsComplianceDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className={styles.header}>
      {/* Left: Profile Info */}
      <div className={styles.leftSection}>
        <div className={styles.profileInfo}>
          <div className={styles.profileImage}>
            <img
              src={profilePicUrl || "https://via.placeholder.com/40"}
              alt={creatorName}
              className={styles.profileImg}
            />
          </div>
          <div>
            <h3 className={styles.headerTitle}>Script Review</h3>
            <div className={styles.creatorInfo}>
              <span className={styles.creatorName}>{creatorName}</span>
              <span className={styles.creatorSeparator}>•</span>
              <span className={styles.creatorHandle}>@{creatorHandle}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Controls & Close */}
      <div className={styles.rightSection}>
        {/* Compliance Dropdown */}
        <div className={styles.dropdownWrapper} ref={complianceDropdownRef}>
          <button
            onClick={() =>
              setIsComplianceDropdownOpen(!isComplianceDropdownOpen)
            }
            className={classNames(styles.complianceButton, {
              [styles.complianceCompliant]:
                complianceStatusConfig.tone === "success",
              [styles.complianceNonCompliant]:
                complianceStatusConfig.tone === "error" ||
                complianceStatusConfig.tone === "warning",
            })}
          >
            <Sparkles
              size={12}
              fill="currentColor"
              className={styles.complianceIcon}
            />
            <span>AI Review: {complianceStatusConfig.label}</span>
            <ChevronDown
              size={12}
              className={classNames(styles.chevronIcon, {
                [styles.chevronRotated]: isComplianceDropdownOpen,
              })}
            />
          </button>

          {isComplianceDropdownOpen && (
            <div className={styles.complianceDropdown}>
              {/* Dropdown Header */}
              <div className={styles.complianceDropdownHeader}>
                <div className={styles.complianceScoreWrap}>
                  <div className={styles.complianceScoreValue}>
                    {isRunningAnalysis ? 0 : complianceScore}%
                  </div>
                  <div className={styles.complianceScoreLabel}>
                    Compliance
                    <br />
                    Score
                  </div>
                </div>
                <div className={styles.complianceStatusBadge}>
                  <span
                    className={classNames(styles.complianceStatusTag, {
                      [styles.complianceStatusCompliant]:
                        complianceStatusConfig.tone === "success",
                      [styles.complianceStatusNonCompliant]:
                        complianceStatusConfig.tone === "error" ||
                        complianceStatusConfig.tone === "warning",
                    })}
                  >
                    {complianceStatusConfig.label}
                  </span>
                </div>
              </div>

              {/* Dropdown Body */}
              <div className={styles.complianceDropdownBody}>
                {complianceReport && !isRunningAnalysis ? (
                  !(
                    complianceReport.isAiAdvancedPromptingChecks ||
                    complianceReport.isAiBrandGuardRailsChecks ||
                    complianceReport.isAiPerformanceChecks ||
                    complianceReport.isAiConsistencyCheck ||
                    complianceReport.isAiSafetyChecks ||
                    complianceReport.isAiScriptVsDraftingConsistencyCheck
                  ) ? (
                    <>
                      <div className={styles.complianceEmptyTitle}>
                        No compliance checks enabled
                      </div>
                      <div className={styles.complianceEmptySubtitle}>
                        Enable compliance checks to see flagged items.
                      </div>
                    </>
                  ) : aiBrandGuardRailsChecks.every(
                      (check) =>
                        check.status ===
                        ComplianceCheckStatusReportEnum.Compliant
                    ) &&
                    aiAdvancedPromptingChecks.every(
                      (check) =>
                        check.status ===
                        ComplianceCheckStatusReportEnum.Compliant
                    ) &&
                    aiPerformanceChecks.every(
                      (check) =>
                        check.status ===
                        ComplianceCheckStatusReportEnum.Compliant
                    ) &&
                    aiConsistencyChecks.every(
                      (check) =>
                        check.status ===
                        ComplianceCheckStatusReportEnum.Compliant
                    ) &&
                    aiSafetyChecks.every(
                      (check) =>
                        check.status ===
                        ComplianceCheckStatusReportEnum.Compliant
                    ) &&
                    aiScriptVsDraftingConsistencyChecks.every(
                      (check) =>
                        check.status ===
                        ComplianceCheckStatusReportEnum.Compliant
                    ) &&
                    false ? (
                    <div className={styles.complianceAllClear}>
                      All checks passed
                    </div>
                  ) : (
                    <>
                      {isAiConsistencyCheck &&
                        renderGuidelineSection(
                          "Consistency Checks",
                          aiConsistencyChecks.map((check) => ({
                            ...check,
                            status:
                              check.status as ComplianceCheckStatusReportEnum,
                          }))
                        )}
                      {isAiScriptVsDraftingConsistencyCheck &&
                        renderGuidelineSection(
                          "Script vs Draft",
                          aiScriptVsDraftingConsistencyChecks.map((check) => ({
                            ...check,
                            status:
                              check.status as ComplianceCheckStatusReportEnum,
                          }))
                        )}
                      {isAiSafetyChecks &&
                        renderGuidelineSection(
                          "Safety & Guidelines",
                          aiSafetyChecks.map((check) => ({
                            ...check,
                            status:
                              check.status as ComplianceCheckStatusReportEnum,
                          }))
                        )}
                      {isAiPerformanceChecks &&
                        renderGuidelineSection(
                          "Performance Checks",
                          aiPerformanceChecks.map((check) => ({
                            ...check,
                            status:
                              check.status as ComplianceCheckStatusReportEnum,
                          }))
                        )}
                      {isAiBrandGuardRailsChecks &&
                        renderGuidelineSection(
                          "Brand Guardrails",

                          aiBrandGuardRailsChecks.map((check) => ({
                            ...check,
                            status:
                              check.status as ComplianceCheckStatusReportEnum,
                          }))
                        )}
                      {isAiSafetyChecks &&
                        renderGuidelineSection(
                          "Advanced Prompting",

                          aiAdvancedPromptingChecks.map((check) => ({
                            ...check,
                            status:
                              check.status as ComplianceCheckStatusReportEnum,
                          }))
                        )}
                    </>
                  )
                ) : (
                  <div className={styles.complianceEmpty}>
                    {isRunningAnalysis ? (
                      <Flex align="center" gap={8}>
                        <Loader2 size={14} className={styles.spin} />
                        <span>
                          {complianceMessages[complianceMessageIndex]}
                        </span>
                      </Flex>
                    ) : (
                      <>
                        <div className={styles.complianceEmptyTitle}>
                          No compliance report yet
                        </div>
                        <div className={styles.complianceEmptySubtitle}>
                          Run the compliance check to see flagged items.
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Dropdown Footer */}
              <div className={styles.complianceDropdownFooter}>
                <div className={styles.complianceFooterLeft}>
                  {failCount > 0 && (
                    <>
                      <div className={styles.complianceFooterDot}></div>
                      <span className={styles.complianceFooterText}>
                        {failCount}{" "}
                        {failCount === 1 ? "review failed" : "reviews failed"}
                      </span>
                    </>
                  )}
                </div>
                <Space size={2} align="center">
                  {/* <Button
                    type="default"
                    size="small"
                    onClick={() => {
                      window.open(ROUTES.AI_CONTROLS, "_blank");
                    }}
                  >
                    View AI control settings
                  </Button> */}

                  <button
                    onClick={() => {
                      if (!hasComplianceParams) return;
                      generateScriptCompliance(complianceParams);
                    }}
                    disabled={!hasComplianceParams || isRunningAnalysis}
                    className={styles.complianceRefreshButton}
                    title="Rerun Analysis"
                  >
                    {isRunningAnalysis ? (
                      <Loader2 size={14} className={styles.spin} />
                    ) : (
                      <RotateCw size={14} />
                    )}
                  </button>
                </Space>
              </div>
            </div>
          )}
        </div>

        <div className={styles.divider}></div>

        {onClose && (
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        )}
      </div>
    </header>
  );
}

const renderGuidelineSection = (
  title: string,
  items: {
    point: string;
    status: ComplianceCheckStatusReportEnum;
    reason?: string;
  }[]
) => {
  if (items.length === 0) return null;
  return (
    <div key={`${title}`} className={styles.complianceCheckCard}>
      <div className={styles.checkCardHeader}>
        <span className={styles.checkCardTitle}>{title}</span>
      </div>
      <Flex vertical gap={8}>
        {items?.map((item, idx) => {
          const isPass =
            item.status === ComplianceCheckStatusReportEnum.Compliant;
          const isWarning =
            item.status === ComplianceCheckStatusReportEnum.PartiallyCompliant;
          const isError =
            item.status === ComplianceCheckStatusReportEnum.NonCompliant;

          return (
            <>
              {isPass ? (
                <>
                  {item.point && (
                    <div className={styles.checkPassHeader}>
                      <CheckCircle2
                        size={10}
                        className={styles.checkPassIcon}
                      />
                      <div className={styles.checkPassText}>{item.point}</div>
                    </div>
                  )}
                  {item.reason && (
                    <p className={styles.checkPassMessage}>{item.reason}</p>
                  )}
                </>
              ) : (
                <div className={styles.checkIssueBox}>
                  {item.point && (
                    <div className={styles.checkIssueHeader}>
                      <AlertTriangle
                        size={10}
                        className={styles.checkIssueIcon}
                      />
                      <div className={styles.checkIssueText}>{item.point}</div>
                    </div>
                  )}
                  {item.reason && (
                    <div className={styles.checkIssueReason}>{item.reason}</div>
                  )}
                </div>
              )}
            </>
          );
        })}
      </Flex>
    </div>
  );
};
